import React from 'react';
import { Link } from 'react-router-dom';

export default function DraenContentSection() {
  const [activeSolution, setActiveSolution] = React.useState('omfangsdraen'); // 'omfangsdraen' | 'draenspuling' | 'both'

  const warningSigns = [
    {
      title: 'Fugtige kældervægge og skjolder langs gulvet',
      desc: 'Tydelige tegn på at vand fra den omgivende jord trænger igennem fundament eller murværk.',
      icon: 'fa-duotone fa-house-flood-water',
      badge: 'Kælder & Fundament'
    },
    {
      title: 'Vand tæt ved soklen efter kraftig regn',
      desc: 'Når overfladevand eller utilstrækkeligt fald lader vand samle sig direkte op ad soklen.',
      icon: 'fa-duotone fa-cloud-rain',
      badge: 'Sokkel & Terræn'
    },
    {
      title: 'Bløde områder eller pytter på grunden',
      desc: 'Vidner om at jorden længe er mættet, eller at det naturlige afdræn ikke fungerer tilstrækkeligt.',
      icon: 'fa-duotone fa-water-lower',
      badge: 'Have & Grund'
    },
    {
      title: 'Et eksisterende dræn med svag eller ujævn effekt',
      desc: 'Sand, slam eller rødder kan have tilstoppet drænrørene eller nedsat strømningen over tid.',
      icon: 'fa-duotone fa-gauge-circle-minus',
      badge: 'Eksisterende Dræn'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Forundersøgelse & Vurdering',
      text: 'Vi undersøger fugt, terræn, jord og eksisterende ledninger',
      icon: 'fa-duotone fa-magnifying-glass-location'
    },
    {
      step: '02',
      title: 'Planlægning & Dimensionering',
      text: 'Vi planlægger placering, fald, brønde og afledning',
      icon: 'fa-duotone fa-compass-drafting'
    },
    {
      step: '03',
      title: 'Udgravning & Rørlægning',
      text: 'Vi udfører udgravning, rørlægning og nødvendig tilslutning',
      icon: 'fa-duotone fa-trowel'
    },
    {
      step: '04',
      title: 'Kontrol & Retablering',
      text: 'Vi kontrollerer funktionen og retablerer efter aftale',
      icon: 'fa-duotone fa-badge-check'
    }
  ];

  const priceFactors = [
    { title: 'Adgangsforhold', desc: 'Pladsforhold for gravemaskiner og maskineri omkring bygningen', icon: 'fa-solid fa-truck-ramp-couch' },
    { title: 'Jordtype & Jordbund', desc: 'Lerholdig jord, sandjord eller hård bund påvirker gravearbejdet', icon: 'fa-solid fa-layer-group' },
    { title: 'Gravedybde', desc: 'Dybden til soklens underkant eller kælderniveau afgør omfanget', icon: 'fa-solid fa-arrow-down-to-line' },
    { title: 'Strækning & Længde', desc: 'Antal løbende meter drænrør, der skal etableres eller fornys', icon: 'fa-solid fa-ruler-horizontal' },
    { title: 'Afledningsmetode', desc: 'Tilslutning til faskine, regnvandsbrønd eller offentlig kloak', icon: 'fa-solid fa-pipe-valve' },
    { title: 'Brønde & Retablering', desc: 'Behov for sandfangsbrønde, pumpebrønd samt genetablering af belægning/græs', icon: 'fa-solid fa-shovel' }
  ];

  const serviceTowns = [
    { name: 'Sommersted', isBase: true },
    { name: 'Jels', isBase: true },
    { name: 'Haderslev', isBase: false },
    { name: 'Vojens', isBase: false },
    { name: 'Vejen', isBase: false },
    { name: 'Kolding', isBase: false },
    { name: 'Gram', isBase: false }
  ];

  return (
    <div className="space-y-12">
      {/* Intro Banner */}
      <div className="rounded-xl border border-brand-orange-500/20 bg-gradient-to-br from-brand-navy-900 to-brand-navy-950 p-6 md:p-8 text-white shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 w-36 h-36 bg-brand-orange-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-3 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-orange-500/20 border border-brand-orange-400/30 text-brand-orange-300 text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-shield-check" /> Professionel fugtsikring
            </div>
            <h2 className="font-display text-xl sm:text-2xl md:text-[22px] lg:text-[21px] xl:text-[25px] font-bold tracking-tight text-white leading-snug xl:whitespace-nowrap [text-wrap:balance]">
              Dræn der beskytter hus og grund mod fugt
            </h2>
            <p className="text-brand-navy-200 text-base md:text-lg leading-relaxed">
              Har du fugt ved soklen, vand i kælderen eller bløde områder på grunden, kan et korrekt etableret dræn være en vigtig del af løsningen. Entreprenør Jan Nørgaard har mere end 20 års erfaring og hjælper private og erhverv med vurdering, etablering og vedligeholdelse af dræn.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row md:flex-col gap-3 w-full md:w-auto">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center justify-center gap-2 rounded bg-brand-orange-500 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-brand-orange-600 transition-colors shadow-md text-center"
            >
              <i className="fa-solid fa-phone" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center gap-2 rounded border border-white/20 bg-white/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-white/20 transition-colors text-center"
            >
              <i className="fa-solid fa-calculator" /> Indhent tilbud
            </Link>
          </div>
        </div>
      </div>

      {/* Sektion 1: Dræn omkring hus, hvornår er det den rigtige løsning? */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500/10 text-brand-orange-500 flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-house-chimney-crack" />
          </div>
          <h3 className="font-display text-2xl font-bold text-brand-navy-900">
            Dræn omkring hus, hvornår er det den rigtige løsning?
          </h3>
        </div>

        <div className="text-brand-navy-700 leading-relaxed space-y-4 text-base md:text-lg">
          <p>
            Fugt kan komme fra flere steder, og derfor starter et godt resultat med at finde årsagen. Højtstående grundvand, overfladevand, terrænets fald eller et gammelt dræn kan give lignende tegn. Hos Entreprenør Jan Nørgaard vurderer vi forholdene omkring bygningen, før vi anbefaler et nyt dræn omkring hus og sokkel.
          </p>
          <p>
            Et velfungerende drænsystem hænger ofte uløseligt sammen med tilstødende arealer: en professionel rensning via{' '}
            <Link
              to="/ydelser/kloakspuling"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              kloakspuling
            </Link>{' '}
            kan være nødvendig for at fjerne propper i tilledninger, mens efterfølgende genetablering af have og{' '}
            <Link
              to="/ydelser/belaegning"
              className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
            >
              belægning
            </Link>{' '}
            sikrer, at regnvand effektivt ledes væk fra soklen.
          </p>
          <p>
            Et omfangsdræn opsamler vand i jorden langs fundamentet og leder det videre til en egnet løsning. Det kan være relevant ved fugtbelastede kældervægge og sokler, men det løser ikke alle fugtproblemer. Konstruktion, jordbund og mulighederne for afledning skal altid ses samlet.
          </p>
        </div>

        {/* Advarselstegn Callout & Kort */}
        <div className="mt-8 pt-6 border-t border-brand-navy-100">
          <div className="bg-amber-50/80 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-triangle-exclamation text-amber-600 mt-1 text-lg shrink-0" />
              <p className="text-sm md:text-base text-amber-900 leading-relaxed">
                Vær især opmærksom, hvis fugten vender tilbage efter regn, hvis der står vand tæt ved huset, eller hvis jorden længe føles mættet. Tegnene fortæller ikke alene, hvilken løsning der er rigtig, men de er en god grund til at få forholdene undersøgt, inden belastningen bliver større.
              </p>
            </div>
          </div>

          <h4 className="font-display text-base uppercase tracking-wider font-bold text-brand-navy-900 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-list-check text-brand-orange-500" />
            4 typiske tegn på at drænet bør undersøges:
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {warningSigns.map((item, idx) => (
              <div
                key={idx}
                className="group relative rounded-lg border border-brand-navy-100 bg-brand-navy-50/50 p-4 transition-all hover:bg-white hover:shadow-md hover:border-brand-orange-500/30"
              >
                <div className="flex items-start gap-3.5">
                  <div className="h-10 w-10 shrink-0 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-lg">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-brand-orange-600 bg-brand-orange-50 px-2 py-0.5 rounded">
                      {item.badge}
                    </span>
                    <h5 className="font-display text-base font-bold text-brand-navy-900 mt-1">
                      {item.title}
                    </h5>
                    <p className="mt-1 text-xs md:text-sm text-brand-navy-600 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion 2: Korrekt opbygning af dræn gør hele forskellen */}
      <section className="bg-brand-navy-900 text-white rounded-xl p-6 md:p-8 shadow-md relative overflow-hidden">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-lg bg-brand-orange-500 text-white flex items-center justify-center text-xl shrink-0">
            <i className="fa-duotone fa-layer-group" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-400">
              Kvalitet & Præcision
            </span>
            <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Korrekt opbygning af dræn gør hele forskellen
            </h3>
          </div>
        </div>

        <div className="text-brand-navy-200 text-base md:text-lg leading-relaxed space-y-4">
          <p>
            Korrekt opbygning af dræn handler om mere end at lægge et rør i jorden. Placering, dybde, fald, filtermateriale og adgang til kontrol skal passe til ejendommen. Samtidig skal det opsamlede vand kunne ledes væk på en lovlig og holdbar måde, uden at skabe problemer et andet sted.
          </p>
        </div>

        {/* 4-Trins Proces Visualisering */}
        <div className="mt-8 pt-6 border-t border-brand-navy-800">
          <h4 className="font-display text-sm uppercase tracking-wider font-bold text-brand-orange-400 mb-6 flex items-center gap-2">
            <i className="fa-solid fa-arrow-progress" /> Vores arbejdsproces fra start til slut:
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {processSteps.map((step, idx) => (
              <div
                key={idx}
                className="bg-brand-navy-800/80 rounded-lg p-5 border border-brand-navy-700/80 relative flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-display text-2xl font-black text-brand-orange-500">
                      {step.step}
                    </span>
                    <i className={`${step.icon} text-brand-navy-400 text-xl`} />
                  </div>
                  <h5 className="font-display text-base font-bold text-white mb-2">
                    {step.title}
                  </h5>
                  <p className="text-xs md:text-sm text-brand-navy-300 leading-relaxed">
                    {step.text}
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-brand-navy-700 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-orange-300">
                  <i className="fa-solid fa-check text-xs" /> Færdigfase
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-lg bg-brand-navy-950/60 p-5 border border-brand-navy-800 flex items-start gap-4">
          <div className="h-9 w-9 rounded-full bg-brand-orange-500/20 text-brand-orange-400 flex items-center justify-center shrink-0 mt-0.5">
            <i className="fa-solid fa-excavator text-sm" />
          </div>
          <p className="text-sm md:text-base text-brand-navy-200 leading-relaxed">
            Ved nedgravning af dræn tager vi højde for eksisterende ledninger, fundamentets tilstand og pladsen omkring bygningen. Som entreprenør kan vi håndtere udgravning, jordarbejde og efterfølgende reetablering af fliser og{' '}
            <Link
              to="/ydelser/belaegning"
              className="text-brand-orange-400 font-semibold underline decoration-brand-orange-400/60 underline-offset-2 hover:text-brand-orange-300 transition-colors"
            >
              belægning
            </Link>{' '}
            som en samlet del af opgaven. Det giver et sammenhængende forløb fra første vurdering til færdig aflevering.
          </p>
        </div>
      </section>

      {/* Sektion 3: Interaktiv Løsningsguide: Omfangsdræn vs. Drænspuling */}
      <div className="bg-white rounded-2xl border border-brand-navy-200/80 shadow-sm overflow-hidden">
        {/* Header & Tab-Navigation */}
        <div className="bg-gradient-to-b from-brand-navy-50/70 via-white to-white px-6 pt-7 pb-5 border-b border-brand-navy-100">
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600 flex items-center gap-1.5">
                  <i className="fa-solid fa-compass-drafting text-brand-orange-500" />
                  Specialiserede drænløsninger
                </span>
                <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-navy-900 mt-1">
                  Etablering eller vedligeholdelse af dræn?
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-brand-navy-500 max-w-md md:text-right">
                <span className="font-semibold text-brand-navy-800 block sm:inline">Vælg løsning forneden:</span> Klik på en af de to faner for at skifte visning og læse om løsningen.
              </p>
            </div>

            {/* To tydelige, overskuelige faner */}
            <div 
              role="tablist"
              aria-label="Vælg drænløsning"
              className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 p-2 bg-brand-navy-100/80 rounded-xl border border-brand-navy-200/80 shadow-inner"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeSolution === 'omfangsdraen'}
                aria-controls="panel-omfangsdraen"
                onClick={() => setActiveSolution('omfangsdraen')}
                className={`group relative flex items-center justify-between gap-3 p-3.5 rounded-lg text-left transition-all cursor-pointer ${
                  activeSolution === 'omfangsdraen'
                    ? 'bg-white text-brand-navy-950 shadow-md border-2 border-brand-orange-500 ring-2 ring-brand-orange-500/20 scale-[1.01]'
                    : 'bg-white/60 text-brand-navy-700 hover:text-brand-navy-950 hover:bg-white hover:shadow-xs border border-brand-navy-200/70'
                }`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div
                    className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                      activeSolution === 'omfangsdraen'
                        ? 'bg-brand-orange-500 text-white shadow-xs'
                        : 'bg-brand-navy-200/60 text-brand-navy-600 group-hover:bg-brand-orange-100 group-hover:text-brand-orange-600'
                    }`}
                  >
                    <i className="fa-solid fa-pipe-valve text-lg" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm sm:text-base leading-snug text-brand-navy-950 flex items-center gap-1.5">
                      1. Omfangsdræn
                    </div>
                    <div className="text-xs text-brand-navy-600 font-medium truncate sm:whitespace-normal">
                      Permanent sikring af fundament & kælder
                    </div>
                  </div>
                </div>

                <div className="shrink-0 pl-1">
                  {activeSolution === 'omfangsdraen' ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-brand-orange-50 text-brand-orange-700 border border-brand-orange-300 shadow-2xs">
                      <i className="fa-solid fa-circle-check text-brand-orange-600 text-[11px]" />
                      <span>Vises nu</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-brand-navy-700 border border-brand-navy-200/80 group-hover:border-brand-orange-400 group-hover:text-brand-orange-600 group-hover:bg-brand-orange-50/50 shadow-2xs transition-colors">
                      <i className="fa-regular fa-hand-pointer text-brand-orange-500 text-[11px]" />
                      <span>Tryk for at læse</span>
                    </span>
                  )}
                </div>
              </button>

              <button
                type="button"
                role="tab"
                aria-selected={activeSolution === 'draenspuling'}
                aria-controls="panel-draenspuling"
                onClick={() => setActiveSolution('draenspuling')}
                className={`group relative flex items-center justify-between gap-3 p-3.5 rounded-lg text-left transition-all cursor-pointer ${
                  activeSolution === 'draenspuling'
                    ? 'bg-white text-brand-navy-950 shadow-md border-2 border-blue-600 ring-2 ring-blue-500/20 scale-[1.01]'
                    : 'bg-white/60 text-brand-navy-700 hover:text-brand-navy-950 hover:bg-white hover:shadow-xs border border-brand-navy-200/70'
                }`}
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div
                    className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                      activeSolution === 'draenspuling'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-brand-navy-200/60 text-brand-navy-600 group-hover:bg-blue-100 group-hover:text-blue-600'
                    }`}
                  >
                    <i className="fa-solid fa-spray-can text-lg" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-sm sm:text-base leading-snug text-brand-navy-950 flex items-center gap-1.5">
                      2. Drænspuling
                    </div>
                    <div className="text-xs text-brand-navy-600 font-medium truncate sm:whitespace-normal">
                      Rensning, inspektion & frit gennemløb
                    </div>
                  </div>
                </div>

                <div className="shrink-0 pl-1">
                  {activeSolution === 'draenspuling' ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-300 shadow-2xs">
                      <i className="fa-solid fa-circle-check text-blue-600 text-[11px]" />
                      <span>Vises nu</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-white text-brand-navy-700 border border-brand-navy-200/80 group-hover:border-blue-400 group-hover:text-blue-600 group-hover:bg-blue-50/50 shadow-2xs transition-colors">
                      <i className="fa-regular fa-hand-pointer text-blue-500 text-[11px]" />
                      <span>Tryk for at læse</span>
                    </span>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Fane-indhold */}
        <div className="p-6 md:p-8">
          {/* VISNING 1: OMFANGSDRÆN ALENE */}
          {activeSolution === 'omfangsdraen' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-700 bg-brand-orange-50 border border-brand-orange-200/60 px-2.5 py-1 rounded-md">
                    <i className="fa-solid fa-award" /> Autoriseret Kloakmester
                  </span>
                  <span className="text-xs text-brand-navy-500 font-medium">
                    Permanent løsning til sokkel & kælder
                  </span>
                </div>

                <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
                  Omfangsdræn kræver blik for hele ejendommen
                </h3>

                <p className="text-brand-navy-700 text-base leading-relaxed">
                  Et omfangsdræn ligger langs husets fundament og skal placeres ud fra de konkrete konstruktioner og koter. Et forkert niveau eller utilstrækkeligt fald kan begrænse virkningen. Derfor må løsningen ikke baseres på en standard, men skal planlægges efter bygningen, jorden og afledningen.
                </p>

                <p className="text-brand-navy-700 text-base leading-relaxed">
                  Da drænvand ofte skal kobles til en afløbsløsning, kan der være krav til tilladelse og autoriseret arbejde. Som autoriseret kloakmester sørger vi for at vurdere de relevante forhold og udføre den del af arbejdet, der kræver autorisation, efter gældende regler.
                </p>

                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-bold text-brand-orange-600 hover:text-brand-orange-700 group"
                  >
                    <span>Få vurderet dit behov for omfangsdræn</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Faktaboks højre kolonne */}
              <div className="lg:col-span-5 bg-brand-navy-50/70 border border-brand-navy-200/80 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-brand-navy-200/60">
                  <div className="h-8 w-8 rounded-lg bg-brand-orange-500/10 text-brand-orange-600 flex items-center justify-center">
                    <i className="fa-duotone fa-ruler-combined" />
                  </div>
                  <h4 className="font-display font-bold text-brand-navy-900 text-sm">
                    Vigtige faglige hensyn ved omfangsdræn
                  </h4>
                </div>

                <ul className="space-y-3 text-xs sm:text-sm text-brand-navy-700">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange-500 mt-1 shrink-0" />
                    <span><strong>Koter & niveauer:</strong> Drænet projekteres i korrekt dybde med præcist fald langs fundamentet.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange-500 mt-1 shrink-0" />
                    <span><strong>Jord- og filteropbygning:</strong> Sikrer at overfladevand og jordtryk filtreres uden at tilstoppe rørene.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-brand-orange-500 mt-1 shrink-0" />
                    <span><strong>Autoriseret kloakarbejde:</strong> Korrekt og godkendt tilslutning til samlebrønd eller regnvandskloak.</span>
                  </li>
                </ul>

                <div className="pt-3 border-t border-brand-navy-200/60 flex items-center gap-2 text-xs font-bold text-brand-navy-600">
                  <i className="fa-solid fa-shield-check text-brand-orange-500" />
                  <span>Fuld dokumentation og lovlig udførelse</span>
                </div>
              </div>
            </div>
          )}

          {/* VISNING 2: DRÆNSPULING ALENE */}
          {activeSolution === 'draenspuling' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-7 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200/60 px-2.5 py-1 rounded-md">
                    <i className="fa-solid fa-spray-can" /> Spuling & Inspektion
                  </span>
                  <span className="text-xs text-brand-navy-500 font-medium">
                    Genopretning uden opgravning
                  </span>
                </div>

                <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
                  Drænspuling kan genskabe et frit gennemløb
                </h3>

                <p className="text-brand-navy-700 text-base leading-relaxed">
                  Et ældre dræn kan miste effekt, når sand, slam eller aflejringer begrænser gennemløbet. Drænspuling kan i mange tilfælde løsne materialet og skabe fri passage igen. Vi vurderer først adgangen og anlæggets tilstand, så spulingen udføres med en metode, der passer til rørene.
                </p>

                <p className="text-brand-navy-700 text-base leading-relaxed">
                  Hvis problemet vender tilbage, kan det skyldes skader, sætninger eller en uhensigtsmæssig opbygning. Her kan en inspektion med kamera være relevant som supplement til drænspuling. Undersøgelsen giver et bedre grundlag for at afgøre, om drænet skal renses, repareres eller helt eller delvist fornyes.
                </p>

                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 group"
                  >
                    <span>Bestil drænspuling eller TV-inspektion</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Faktaboks højre kolonne */}
              <div className="lg:col-span-5 bg-blue-50/50 border border-blue-200/70 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-blue-200/60">
                  <div className="h-8 w-8 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center">
                    <i className="fa-duotone fa-camera-cctv" />
                  </div>
                  <h4 className="font-display font-bold text-brand-navy-900 text-sm">
                    Fordele ved spuling og inspektion
                  </h4>
                </div>

                <ul className="space-y-3 text-xs sm:text-sm text-brand-navy-700">
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-blue-600 mt-1 shrink-0" />
                    <span><strong>Skånsom rensning:</strong> Fjerner effektivt sand, rødder og slam uden behov for at grave haven op.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-blue-600 mt-1 shrink-0" />
                    <span><strong>TV-kamera:</strong> Giver et klart indblik i drænets reelle tilstand og lokaliserer brud eller sætninger.</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="fa-solid fa-check text-blue-600 mt-1 shrink-0" />
                    <span><strong>Målrettet udbedring:</strong> Sikrer at du kun reparerer eller udskifter det, der rent faktisk er nødvendigt.</span>
                  </li>
                </ul>

                <div className="pt-3 border-t border-blue-200/60 flex items-center gap-2 text-xs font-bold text-brand-navy-600">
                  <i className="fa-solid fa-circle-check text-blue-600" />
                  <span>Effektiv afhjælpning uden unødig opgravning</span>
                </div>
              </div>
            </div>
          )}

          {/* VISNING 3: SAMMENLIGN BEGGE SIDE OM SIDE */}
          {activeSolution === 'both' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Omfangsdræn kort */}
              <div className="bg-brand-navy-50/50 rounded-xl border border-brand-orange-200/80 p-6 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-700 bg-brand-orange-50 px-2.5 py-1 rounded">
                      <i className="fa-solid fa-award" /> Autoriseret Kloakmester
                    </span>
                    <i className="fa-duotone fa-pipe-valve text-2xl text-brand-orange-500" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-brand-navy-900">
                    Omfangsdræn kræver blik for hele ejendommen
                  </h3>
                  <p className="text-brand-navy-700 text-sm leading-relaxed">
                    Et omfangsdræn ligger langs husets fundament og skal placeres ud fra de konkrete konstruktioner og koter. Et forkert niveau eller utilstrækkeligt fald kan begrænse virkningen. Derfor må løsningen ikke baseres på en standard, men skal planlægges efter bygningen, jorden og afledningen.
                  </p>
                  <p className="text-brand-navy-700 text-sm leading-relaxed">
                    Da drænvand ofte skal kobles til en afløbsløsning, kan der være krav til tilladelse og autoriseret arbejde. Som autoriseret kloakmester sørger vi for at vurdere de relevante forhold og udføre den del af arbejdet, der kræver autorisation, efter gældende regler.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-brand-orange-200/60 flex items-center gap-2 text-xs font-bold text-brand-navy-700">
                  <i className="fa-solid fa-shield-check text-brand-orange-500 text-sm" />
                  Fuld dokumentation og lovlig udførelse
                </div>
              </div>

              {/* Drænspuling kort */}
              <div className="bg-blue-50/30 rounded-xl border border-blue-200/80 p-6 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-blue-700 bg-blue-50 px-2.5 py-1 rounded">
                      <i className="fa-solid fa-spray-can" /> Spuling & Inspektion
                    </span>
                    <i className="fa-duotone fa-camera-cctv text-2xl text-blue-600" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-brand-navy-900">
                    Drænspuling kan genskabe et frit gennemløb
                  </h3>
                  <p className="text-brand-navy-700 text-sm leading-relaxed">
                    Et ældre dræn kan miste effekt, når sand, slam eller aflejringer begrænser gennemløbet. Drænspuling kan i mange tilfælde løsne materialet og skabe fri passage igen. Har du desuden behov for rensning af stikledninger, brønde eller afløb, udfører vi også autoriseret{' '}
                    <Link
                      to="/ydelser/kloakspuling"
                      className="text-brand-orange-600 font-semibold underline decoration-brand-orange-500/50 underline-offset-2 hover:text-brand-orange-700 transition-colors"
                    >
                      kloakspuling
                    </Link>{' '}
                    med specialdyser. Vi vurderer først adgangen og anlæggets tilstand, så spulingen udføres med en metode, der passer til rørene.
                  </p>
                  <p className="text-brand-navy-700 text-sm leading-relaxed">
                    Hvis problemet vender tilbage, kan det skyldes skader, sætninger eller en uhensigtsmæssig opbygning. Her kan en inspektion med kamera være relevant som supplement til drænspuling. Undersøgelsen giver et bedre grundlag for at afgøre, om drænet skal renses, repareres eller helt eller delvist fornyes.
                  </p>
                </div>
                <div className="mt-6 pt-4 border-t border-blue-200/60 flex items-center gap-2 text-xs font-bold text-brand-navy-700">
                  <i className="fa-solid fa-circle-check text-blue-600 text-sm" />
                  Effektiv afhjælpning uden unødig opgravning
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sektion 4: Prisfaktorer (Overskuelig opstilling) */}
      <section className="bg-white rounded-xl border border-brand-navy-100 p-6 md:p-8 shadow-xs">
        <div className="max-w-3xl mb-6">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
            Gennemskuelighed
          </span>
          <h3 className="font-display text-2xl font-bold text-brand-navy-900 mt-1">
            Hvad afgør prisen på et nyt dræn?
          </h3>
          <p className="mt-2 text-brand-navy-700 text-base leading-relaxed">
            Prisen på et nyt dræn afhænger blandt andet af adgangsforhold, jordtype, gravedybde, strækning og den valgte afledning. Behovet for brønde, reparationer og retablering spiller også ind. Derfor ser vi på ejendommen og opgaven, før vi giver et konkret tilbud.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
          {priceFactors.map((factor, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-brand-navy-100 bg-brand-navy-50/40 p-3.5 md:p-4 flex flex-col justify-between"
            >
              <div>
                <div className="h-8 w-8 rounded bg-brand-navy-900 text-brand-orange-400 flex items-center justify-center text-sm mb-2.5">
                  <i className={factor.icon} />
                </div>
                <h4 className="font-display text-sm md:text-base font-bold text-brand-navy-900">
                  {factor.title}
                </h4>
              </div>
              <p className="mt-1.5 text-[11px] md:text-xs text-brand-navy-600 leading-snug">
                {factor.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Interne SEO links til beslægtede ydelser */}
      <section className="bg-white rounded-xl border border-brand-navy-200/90 p-6 md:p-8 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6 pb-4 border-b border-brand-navy-100">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-orange-600">
              Relaterede SEO ydelser
            </span>
            <h3 className="font-display text-xl md:text-2xl font-bold text-brand-navy-900 mt-0.5">
              Se også vores beslægtede fagområder
            </h3>
          </div>
          <p className="text-xs md:text-sm text-brand-navy-500">
            Samlet ekspertise inden for kloak, vandafledning og anlæg
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/ydelser/kloakspuling"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-truck-droplet" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om kloakspuling <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Professionel Kloakspuling
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Effektiv rensning af stoppede kloakrør, fedtpropper, drænledninger og samlebrønde. Vi genskaber det frie gennemløb hurtigt og skånsomt.
              </p>
            </div>
          </Link>

          <Link
            to="/ydelser/belaegning"
            className="group p-5 rounded-xl border border-brand-navy-200/80 bg-brand-navy-50/40 hover:bg-white hover:border-brand-orange-400 hover:shadow-md transition-all flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="h-11 w-11 rounded-lg bg-brand-navy-900 text-brand-orange-400 group-hover:bg-brand-orange-500 group-hover:text-white transition-colors flex items-center justify-center text-xl shadow-xs">
                  <i className="fa-duotone fa-grid-horizontal" />
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 group-hover:translate-x-1 transition-transform">
                  Læs om belægning <i className="fa-solid fa-arrow-right text-[11px]" />
                </span>
              </div>
              <h4 className="font-display text-lg font-bold text-brand-navy-900 group-hover:text-brand-orange-600 transition-colors">
                Belægning & Flisearbejde
              </h4>
              <p className="mt-2 text-sm text-brand-navy-600 leading-relaxed">
                Korrekt underlag, bærelag og faldsikring ved nye terrasser, indkørsler og reetablering af fliser efter dræn- eller kloakarbejde.
              </p>
            </div>
          </Link>
        </div>
      </section>

      {/* Sektion 5: Få lokal hjælp til dræn i Sommersted og omegn */}
      <section className="rounded-xl border border-brand-navy-200 bg-gradient-to-br from-white to-brand-navy-50/50 p-6 md:p-8 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center justify-between">
          <div className="space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-navy-900 text-white text-xs font-bold uppercase tracking-wider">
              <i className="fa-solid fa-location-dot text-brand-orange-400" />
              Lokal dækning i Sønderjylland
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold text-brand-navy-900">
              Få lokal hjælp til dræn i Sommersted og omegn
            </h3>
            <p className="text-brand-navy-700 text-base md:text-lg leading-relaxed">
              Vi har base i området ved Sommersted og Jels og hjælper kunder i blandt andet Haderslev, Vojens, Vejen, Kolding og Gram. Den lokale placering gør det enkelt at få en erfaren entreprenør og kloakmester til både mindre drænopgaver og mere omfattende løsninger omkring bygninger og grunde.
            </p>
            <p className="text-brand-navy-700 text-base leading-relaxed">
              Er du i tvivl om et nyt dræn, et omfangsdræn eller drænspuling er det rigtige næste skridt, tager vi gerne en faglig snak om symptomerne og forholdene hos dig. Kontakt Entreprenør Jan Nørgaard, så finder vi en løsning, der passer til ejendommen og leder vandet sikkert videre.
            </p>
          </div>

          <div className="w-full lg:w-72 shrink-0 bg-white p-5 rounded-lg border border-brand-navy-200 shadow-xs">
            <h4 className="font-display text-xs uppercase tracking-wider font-bold text-brand-navy-500 mb-3">
              Vi dækker følgende byer:
            </h4>
            <div className="flex flex-wrap gap-2">
              {serviceTowns.map((town, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider ${
                    town.isBase
                      ? 'bg-brand-orange-500 text-white shadow-xs'
                      : 'bg-brand-navy-100 text-brand-navy-800'
                  }`}
                >
                  <i className={`fa-solid ${town.isBase ? 'fa-house-flag' : 'fa-location-dot text-brand-orange-600'}`} />
                  {town.name}
                  {town.isBase && <span className="text-[10px] opacity-80">(Base)</span>}
                </span>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-brand-navy-100">
              <Link
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 rounded bg-brand-navy-900 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white hover:bg-brand-navy-800 transition-colors text-center"
              >
                Kontakt Jan Nørgaard
                <i className="fa-solid fa-arrow-right text-brand-orange-400" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
